// Constants Imports
import { strings } from '@/lib/data/site/strings';

export const REGEX = {
  // Email regex pattern
  EMAIL: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,

  // Email regex pattern for registering new emails
  // Disallow '+' moving forward due to WeShip issues
  NEW_EMAIL: /^[A-Za-z0-9._%-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,

  // Password regex pattern (at least one uppercase, one lowercase, one digit, and one special character, min 8 characters)
  PASSWORD: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+={}[\]:;"'<>,.?/~`|-])[A-Za-z\d!@#$%^&*()_+={}[\]:;"'<>,.?/~`|-]{8,}$/,

  // Phone number regex pattern (simple, for US phone numbers)
  PHONE: /^(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/,

  // Username regex pattern... allows letters, numbers and certain special characters
  // does NOT allow an @ symbol and period TOGETHER, which will break wordpress registration
  // Previous for reference:  USERNAME: /^(?!.*@.*\.).(?!.*\..*@)[a-zA-Z0-9 @.-]+$/,

  USERNAME: /^[A-Za-z0-9 @._-]{2,}$/,

  // First Name regex pattern
  FIRST_NAME: /^[A-Za-z\s-]{2,}$/,

  // Last Name regex pattern
  LAST_NAME: /^[A-Za-z\s-]{2,}$/,

  // Name regex pattern with Latin-1 supplement (e.g. José, Müller, Ñoño)
  NAME_LATIN_1: /^[A-Za-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF\s'-]{2,}$/,

  // Address regex pattern (with Latin-1 supplement for accented characters)
  POSTAL_ADDRESS: /^[a-zA-Z0-9À-ÿ\s.,#/-]+$/,

  // City regex pattern (with Latin-1 supplement for accented characters)
  POSTAL_CITY: /^[a-zA-ZÀ-ÿ\s.'-]+$/,

  // Postal code regex pattern (US)
  POSTAL_CODE: /^\d{5}(-\d{4})?$/,

  // Date (YYYY-MM-DD) regex pattern
  DATE: /^\d{4}-\d{2}-\d{2}$/,

  // IP address regex pattern (IPv4)
  IP_ADDRESS: /^(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)$/,

  // Credit card number regex pattern (simple, for validation)
  CREDIT_CARD: /^(?:4[0-9]{12}(?:[0-9]{3})?|5[1-5][0-9]{14}|3[47][0-9]{13}|3(?:0[0-5]|[68][0-9])[0-9]{11}|6(?:011|5[0-9]{2})[0-9]{12}|(?:2131|1800|35\d{3})\d{11})$/,

  // KC# regex pattern
  KEY_CLUB_NUMBER: /^[A-Za-z0-9]+$/,

  // Email subject regex pattern - Letters, numbers, spaces, and hyphens and punctuation only
  EMAIL_MESSAGE: /^[A-Za-z0-9\s.,!?'"()-]+$/,

  // Old KC# regex pattern - letters, numbers, and necessary symbols for tel and email
  OLD_KEY_CLUB_NUMBER: /^[A-Za-z0-9@_.()-]+$/,

  // Username of Email regex pattern
  USERNAME_OR_EMAIL: /^[A-Za-z0-9 @_.()+-]+$/,

  // Order notes validation pattern (optional field — allows empty)
  ORDER_NOTES: /^[A-Za-z0-9À-ÿ\s.,!?'"()\-/#@&+:;_]*$/
};

export type REGEXKEY = keyof typeof REGEX;

// Tier info
export const TIERS = {
  tier1: { min: 0, max: 1962, color: strings.loyaltyProgram.nameTier1, image: strings.loyaltyProgram.iconTier1 },
  tier2: { min: 1963, max: 3499, color: strings.loyaltyProgram.nameTier2, image: strings.loyaltyProgram.iconTier2 },
  tier3: { min: 3500, max: 6499, color: strings.loyaltyProgram.nameTier3, image: strings.loyaltyProgram.iconTier3 },
  tier4: { min: 6500, max: Infinity, color: strings.loyaltyProgram.nameTier4, image: strings.loyaltyProgram.iconTier4 },
} as const;

export type TierKey = keyof typeof TIERS;

// States in long form
export const STATES = [
  { label: "Alabama", value: "AL" },
  { label: "Alaska", value: "AK" },
  { label: "Arizona", value: "AZ" },
  { label: "Arkansas", value: "AR" },
  { label: "California", value: "CA" },
  { label: "Colorado", value: "CO" },
  { label: "Connecticut", value: "CT" },
  { label: "Delaware", value: "DE" },
  { label: "Florida", value: "FL" },
  { label: "Georgia", value: "GA" },
  { label: "Hawaii", value: "HI" },
  { label: "Idaho", value: "ID" },
  { label: "Illinois", value: "IL" },
  { label: "Indiana", value: "IN" },
  { label: "Iowa", value: "IA" },
  { label: "Kansas", value: "KS" },
  { label: "Kentucky", value: "KY" },
  { label: "Louisiana", value: "LA" },
  { label: "Maine", value: "ME" },
  { label: "Maryland", value: "MD" },
  { label: "Massachusetts", value: "MA" },
  { label: "Michigan", value: "MI" },
  { label: "Minnesota", value: "MN" },
  { label: "Mississippi", value: "MS" },
  { label: "Missouri", value: "MO" },
  { label: "Montana", value: "MT" },
  { label: "Nebraska", value: "NE" },
  { label: "Nevada", value: "NV" },
  { label: "New Hampshire", value: "NH" },
  { label: "New Jersey", value: "NJ" },
  { label: "New Mexico", value: "NM" },
  { label: "New York", value: "NY" },
  { label: "North Carolina", value: "NC" },
  { label: "North Dakota", value: "ND" },
  { label: "Ohio", value: "OH" },
  { label: "Oklahoma", value: "OK" },
  { label: "Oregon", value: "OR" },
  { label: "Pennsylvania", value: "PA" },
  { label: "Rhode Island", value: "RI" },
  { label: "South Carolina", value: "SC" },
  { label: "South Dakota", value: "SD" },
  { label: "Tennessee", value: "TN" },
  { label: "Texas", value: "TX" },
  { label: "Utah", value: "UT" },
  { label: "Vermont", value: "VT" },
  { label: "Virginia", value: "VA" },
  { label: "Washington", value: "WA" },
  { label: "Washington, D.C.", value: "DC" },
  { label: "West Virginia", value: "WV" },
  { label: "Wisconsin", value: "WI" },
  { label: "Wyoming", value: "WY" },
];

// States in short form
export const STATES_CODES = [
  { label: "AL", value: "AL" },
  { label: "AK", value: "AK" },
  { label: "AZ", value: "AZ" },
  { label: "AR", value: "AR" },
  { label: "CA", value: "CA" },
  { label: "CO", value: "CO" },
  { label: "CT", value: "CT" },
  { label: "DE", value: "DE" },
  { label: "DC", value: "DC" },
  { label: "FL", value: "FL" },
  { label: "GA", value: "GA" },
  { label: "HI", value: "HI" },
  { label: "ID", value: "ID" },
  { label: "IL", value: "IL" },
  { label: "IN", value: "IN" },
  { label: "IA", value: "IA" },
  { label: "KS", value: "KS" },
  { label: "KY", value: "KY" },
  { label: "LA", value: "LA" },
  { label: "ME", value: "ME" },
  { label: "MD", value: "MD" },
  { label: "MA", value: "MA" },
  { label: "MI", value: "MI" },
  { label: "MN", value: "MN" },
  { label: "MS", value: "MS" },
  { label: "MO", value: "MO" },
  { label: "MT", value: "MT" },
  { label: "NE", value: "NE" },
  { label: "NV", value: "NV" },
  { label: "NH", value: "NH" },
  { label: "NJ", value: "NJ" },
  { label: "NM", value: "NM" },
  { label: "NY", value: "NY" },
  { label: "NC", value: "NC" },
  { label: "ND", value: "ND" },
  { label: "OH", value: "OH" },
  { label: "OK", value: "OK" },
  { label: "OR", value: "OR" },
  { label: "PA", value: "PA" },
  { label: "RI", value: "RI" },
  { label: "SC", value: "SC" },
  { label: "SD", value: "SD" },
  { label: "TN", value: "TN" },
  { label: "TX", value: "TX" },
  { label: "UT", value: "UT" },
  { label: "VT", value: "VT" },
  { label: "VA", value: "VA" },
  { label: "WA", value: "WA" },
  { label: "WV", value: "WV" },
  { label: "WI", value: "WI" },
  { label: "WY", value: "WY" },
];

export type User= {
  fullName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
};

export type State = {
  name: string;
  code: string;
};

export const PlaceholderImageWhite = "/images/pages/product-default-gifts.png";
export const PlaceholderImageGrey = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

export const CART_CHANGE_ADDRESS_PARAM = 'changeAddress';
export const CART_CHANGE_ADDRESS_HREF = `/cart?${CART_CHANGE_ADDRESS_PARAM}=1`;

export const OrderCompleteResponseKey: string = 'OrderCompleteResponse';
